/**
 * Server nonces for id tokens (replay protection), shared by the web and native sign-ins: each sign-in fetches a fresh
 * single-use nonce (GET /v1/auth/nonce, 10 minutes, bound to the player) right before the IdP opens. Google gets it
 * as is; Apple gets its SHA-256 hex (Apple's and Firebase's convention: the raw value never travels through Apple).
 * The raw nonce goes to POST /v1/auth/link/:provider next to the token, and the API accepts the claim in either form.
 */
import { ApiHttpError, ROUTES, type ApiClient } from '../api.js';

/** GET /v1/auth/nonce. Null from a server without the route (404), which takes tokens without a nonce. */
export async function fetchAuthNonce(api: ApiClient): Promise<string | null> {
  try {
    return (await api.call<{ nonce: string }>('GET', ROUTES.authNonce)).nonce;
  } catch (e) {
    if (e instanceof ApiHttpError && e.status === 404) return null;
    throw e;
  }
}

/** Lower-case hex SHA-256 of a UTF-8 string: the form of the nonce Apple's request carries. */
export async function sha256Hex(s: string): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(s));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}
